import {
  modelTypeArea,
  modelTypeBinaryClassifier,
  modelTypeCell,
  modelTypesByApiModelValue,
} from 'components/Pages/Jobs/inferenceFieldsOptions';
import { labelColumnName } from 'components/Procedure/useSlideChannelsAndResults/parseHeatmaps';
import { getAssignmentTodosClassNameOptions } from 'components/StudyDashboard/ProceduresPage/Actions/utils';
import { OrchestrationData } from 'interfaces/calculateFeatures';
import { ResultType } from 'interfaces/experimentResults';
import { AttributesTable, slideResultToAttributesTable } from 'interfaces/inferenceArtifacts';
import {
  concat,
  difference,
  filter,
  find,
  forEach,
  get,
  includes,
  isEmpty,
  map,
  range,
  set,
  toNumber,
  uniq,
} from 'lodash';
import { getModelId, ModelPreferencesById } from 'utils/models';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { combineOrchestrationMetadata } from './utils';

export interface ClassConfigs {
  [stain: string]: {
    [modelKey: string]: string[] | number[];
  };
}

export interface OrchestrationMetadata {
  [stain: string]: {
    [attributesTableKey: string]: {
      [modelKey: string]: OrchestrationData[];
    };
  };
}

export interface DeriveClassConfigParams {
  selectedOrchestrations: OrchestrationBySlideByType;
  allSelectedNonModelOrchestrations: OrchestrationBySlideByResultName[];
  selectedAssignments: Record<string, any[]>;
  slidesById: Record<string, { stainingType: string }>;
  modelPreferencesByModelId: ModelPreferencesById;
  classificationModelOptions: { id: string; needBinningParams?: boolean }[];
}

/**
 * Derives a `ClassConfigs` map and orchestration metadata from currently selected orchestrations/results.
 * This is used to populate option lists (e.g. area.label) and must include label options from
 * slide-level tables (e.g. slide_segmentations_table) when available.
 */
export const deriveClassConfigFromSelections = ({
  selectedOrchestrations,
  allSelectedNonModelOrchestrations,
  selectedAssignments,
  slidesById,
  modelPreferencesByModelId,
  classificationModelOptions,
}: DeriveClassConfigParams): {
  classConfig: ClassConfigs;
  orchestrationMetadata: OrchestrationMetadata;
} => {
  const newClassConfig: ClassConfigs = {};
  const newOrchestrationMetadata: OrchestrationMetadata = {};

  forEach(selectedOrchestrations, (orchestrationByModel, slideId) => {
    forEach(orchestrationByModel, (orchestrationData, modelType) => {
      let classes: string[] | number[] = orchestrationData?.model?.classes;
      const modelId = orchestrationData?.model?.modelId ?? getModelId(orchestrationData?.model?.modelUrl);
      const currentModelPreferences = modelPreferencesByModelId?.[modelId];
      const currentModelType =
        orchestrationData?.model?.modelType === modelTypeBinaryClassifier.apiModelValue
          ? modelTypeBinaryClassifier.value
          : currentModelPreferences?.readAs ?? modelTypesByApiModelValue[modelType]?.value ?? modelType;

      // classification intensity model classes
      if (
        currentModelPreferences?.readAs &&
        find(classificationModelOptions, {
          id: currentModelPreferences?.readAs,
        })?.needBinningParams &&
        (currentModelPreferences?.numBins || !isEmpty(currentModelPreferences?.mapValuesToBins))
      ) {
        classes = currentModelPreferences?.mapValuesToBins
          ? map(currentModelPreferences.mapValuesToBins, (mapValuesToBin) => toNumber(mapValuesToBin.bin))
          : map(range(1, currentModelPreferences?.numBins + 1), (bin) => toNumber(bin));
      } else if (currentModelPreferences) {
        classes = currentModelPreferences?.useClassesFromIgnore
          ? difference(classes as string[], currentModelPreferences?.classesToIgnore)
          : currentModelPreferences?.classesToUse ?? (classes as string[]);
      }

      const slideStainingType = slidesById?.[slideId]?.stainingType;
      if (!slideStainingType) {
        return;
      }

      newClassConfig[slideStainingType] = {
        ...newClassConfig[slideStainingType],
        [currentModelType]: classes,
      };

      const resultType = orchestrationData?.orchestration?.params?.resultType as ResultType;
      const data = orchestrationData?.orchestration?.params?.data;

      if (!isEmpty(data) && Boolean(resultType)) {
        const attributesTable = slideResultToAttributesTable[resultType];
        // Because the orchestration data is under orchestration, we need to check if it already exists in newOrchestrationMetadata and combined the data
        const newData = combineOrchestrationMetadata(
          get(newOrchestrationMetadata, [slidesById[slideId].stainingType, attributesTable, resultType], []),
          orchestrationData.orchestration.params.data
        );

        newOrchestrationMetadata[slidesById[slideId].stainingType] = {
          ...newOrchestrationMetadata[slidesById[slideId].stainingType],
          [attributesTable]: {
            ...(newOrchestrationMetadata[slidesById[slideId].stainingType]?.[attributesTable] || {}),
            [resultType]: newData,
          },
        };
      }
    });
  });

  forEach(allSelectedNonModelOrchestrations, (resultNameOrchestrations) => {
    forEach(resultNameOrchestrations, (orchestrationByResultName, slideId) => {
      const slideStainingType = slidesById[slideId].stainingType;
      if (!slideStainingType) {
        return;
      }

      forEach(orchestrationByResultName, (orchestrationData, resultKey) => {
        const data = orchestrationData?.orchestration?.params?.data as OrchestrationData[];
        if (isEmpty(data)) {
          return;
        }

        const resultType = orchestrationData?.orchestration?.params?.resultType as ResultType;
        const attributesTable = slideResultToAttributesTable[resultType];

        // Because the orchestration data is under orchestration, we need to check if it already exists in newOrchestrationMetadata and combined the data
        const existing = get(newOrchestrationMetadata, [slideStainingType, attributesTable, resultKey], []);
        const newData = combineOrchestrationMetadata(existing, data);

        const modelTypeByAttributesTable =
          attributesTable == AttributesTable.AreaTable
            ? modelTypeArea.value
            : // DetectionsTable does not have a model type associated
            attributesTable !== AttributesTable.DetectionsTable
            ? modelTypeCell.value
            : null;

        //   const labelFieldExists = includes(map(orchestrationData, 'label'), labelColumnName);
        // IMPORTANT: labelColumnName is present in `newData`, not in `orchestrationData`.
        const labelFieldExists = includes(map(newData, 'label'), labelColumnName);

        const orchestrationDataForMetadata =
          modelTypeByAttributesTable && labelFieldExists
            ? filter(newData, (field) => field?.label !== labelColumnName)
            : newData;

        newOrchestrationMetadata[slidesById[slideId].stainingType] = {
          ...newOrchestrationMetadata[slidesById[slideId].stainingType],
          [attributesTable]: {
            ...(newOrchestrationMetadata[slidesById[slideId].stainingType]?.[attributesTable] || {}),
            [resultKey]: orchestrationDataForMetadata,
          },
        };

        if (modelTypeByAttributesTable && labelFieldExists) {
          // Add the labelColumnName to the class config according to the attributesTable
          const labelField = find(newData, { label: labelColumnName });
          const labelOptionsToAdd = (labelField?.options as string[]) || [];
          newClassConfig[slideStainingType] = {
            ...newClassConfig[slideStainingType],
            [modelTypeByAttributesTable]: uniq(
              concat(
                (newClassConfig[slideStainingType]?.[modelTypeByAttributesTable] ?? []) as string[],
                labelOptionsToAdd
              )
            ),
          };
        }
      });
    });
  });

  forEach(selectedAssignments, (assignments, stain) => {
    forEach(assignments, (assignment) => {
      const assignmentClasses = getAssignmentTodosClassNameOptions(assignment?.assignment?.todos);
      const classes = assignment.useClassesFromIgnore
        ? difference(assignmentClasses, assignment.classesToIgnore)
        : assignment.classesToUse;

      // if the assignment does not have selected model type, it means it is used as an input, and not override.
      //  in this situation, we only have areas. so we set the modelType to area
      const modelTypeId =
        modelTypesByApiModelValue[assignment.modelType]?.value ?? assignment.modelType ?? modelTypeArea.value;
      set(newClassConfig, [stain, modelTypeId], uniq(concat(newClassConfig[stain]?.[modelTypeId] ?? [], classes)));
    });
  });

  return {
    classConfig: newClassConfig,
    orchestrationMetadata: newOrchestrationMetadata,
  };
};
